import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Car, MapPin, Calendar, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

type RideActivity = {
  id: string;
  status: string;
  ride_type: string;
  pickup_address: string;
  dropoff_address: string;
  distance_km: number;
  estimated_fare: number;
  final_fare: number | null;
  created_at: string;
  completed_at: string | null;
};

type FilterType = 'all' | 'week' | 'month';

const RideActivitiesPage = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState<RideActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');

  useEffect(() => {
    const fetchRides = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase
        .from('ride_requests')
        .select('id, status, ride_type, pickup_address, dropoff_address, distance_km, estimated_fare, final_fare, created_at, completed_at')
        .eq('customer_id', user.id)
        .in('status', ['completed', 'cancelled'])
        .order('created_at', { ascending: false });

      if (filter === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        query = query.gte('created_at', weekAgo.toISOString());
      } else if (filter === 'month') {
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        query = query.gte('created_at', monthAgo.toISOString());
      }

      const { data } = await query;
      setRides((data as RideActivity[]) || []);
      setLoading(false);
    };
    fetchRides();
  }, [filter]);

  const completedRides = rides.filter(r => r.status === 'completed');
  const totalSpent = completedRides.reduce((sum, r) => sum + (r.final_fare || r.estimated_fare || 0), 0);

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'All Time' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/ride')} className="p-1">
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <h1 className="text-lg font-bold text-foreground">Ride Activities</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 p-4">
        <div className="bg-card rounded-xl p-3 text-center border border-border">
          <p className="text-2xl font-bold text-foreground">{completedRides.length}</p>
          <p className="text-xs text-muted-foreground">Completed</p>
        </div>
        <div className="bg-card rounded-xl p-3 text-center border border-border">
          <p className="text-2xl font-bold text-foreground">D{totalSpent.toFixed(0)}</p>
          <p className="text-xs text-muted-foreground">Total Spent</p>
        </div>
        <div className="bg-card rounded-xl p-3 text-center border border-border">
          <p className="text-2xl font-bold text-foreground">{rides.filter(r => r.status === 'cancelled').length}</p>
          <p className="text-xs text-muted-foreground">Cancelled</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 px-4 pb-3">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Rides List */}
      <div className="px-4 pb-20 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : rides.length === 0 ? (
          <div className="text-center py-12">
            <Car className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No rides yet</p>
          </div>
        ) : (
          rides.map(ride => (
            <div key={ride.id} className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${ride.status === 'completed' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-xs text-muted-foreground capitalize">{ride.status}</span>
                </div>
                <span className="font-bold text-foreground">D{(ride.final_fare || ride.estimated_fare)?.toFixed(0)}</span>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                  <p className="text-foreground line-clamp-1">{ride.pickup_address}</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 shrink-0" />
                  <p className="text-foreground line-clamp-1">{ride.dropoff_address}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  {new Date(ride.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="text-xs text-muted-foreground">{ride.distance_km?.toFixed(1)} km</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RideActivitiesPage;
